import { CdkMenu, CdkMenuItem, PARENT_OR_NEW_INLINE_MENU_STACK_PROVIDER } from '@angular/cdk/menu';
import { CdkTrapFocus, FocusKeyManager } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  AfterContentInit,
  QueryList,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

@Directive({
  selector: '[cuiDropdownTriggerFor]',
  standalone: true,
  exportAs: 'cuiDropdownTrigger',
  host: {
    '(click)': 'toggle()',
    '(keydown.enter)': 'toggle($event)',
    '(keydown.space)': 'toggle($event)',
    '[attr.aria-expanded]': 'isOpen()',
    '[attr.aria-haspopup]': '"menu"',
  },
})
export class CuiDropdownTriggerDirective {
  readonly dropdown = input.required<CuiDropdownComponent>({ alias: 'cuiDropdownTriggerFor' });
  readonly isOpen = computed(() => this.dropdown().isOpen());
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  toggle(event?: Event): void {
    event?.preventDefault();
    this.dropdown().toggle(this.elementRef.nativeElement);
  }
}

@Component({
  selector: 'cui-dropdown',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkMenu, CdkTrapFocus, CommonModule],
  providers: [PARENT_OR_NEW_INLINE_MENU_STACK_PROVIDER('vertical')],
  host: {
    class: 'cui-dropdown-host',
  },
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class CuiDropdownComponent implements AfterContentInit {
  private readonly overlay = inject(Overlay);
  @ContentChildren(CdkMenuItem, { descendants: true })
  private readonly menuItems!: QueryList<CdkMenuItem>;
  private keyManager!: FocusKeyManager<CdkMenuItem>;
  protected readonly trigger = signal<HTMLElement | null>(null);
  readonly isOpen = signal(false);
  protected readonly scrollStrategy = this.overlay.scrollStrategies.reposition({ autoClose: true });
  protected readonly menuPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -8,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  ngAfterContentInit(): void {
    this.keyManager = new FocusKeyManager(this.menuItems).withWrap().withHomeAndEnd();
  }

  toggle(trigger: HTMLElement): void {
    if (this.isOpen() && this.trigger() === trigger) {
      this.close();
      return;
    }

    this.trigger.set(trigger);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  protected handleOverlayKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    this.close();
    this.trigger()?.focus({ preventScroll: true });
  }

  protected handleOverlayAttach(): void {
    queueMicrotask(() => this.keyManager?.setFirstItemActive());
  }

  protected handleMenuKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    this.keyManager?.onKeydown(event);
  }
}
